import { useEffect, useState } from 'react';
import { P, match } from 'ts-pattern';

import type { V2BookingStatus } from '@/api/clients/ohip/models';
import { useBookingPaymentStatus } from '@/booking-payment';

import {
  useBookingStatus,
  useBookingStatusQuery,
} from './use-booking-status.hook';
import { useInvalidateBookingSummary } from './use-invalidate-booking-summary.hook';

const defaultPollInterval = 20e3;
const fastPollInterval = 2e3;

/**
 * This hook is responsible for managing the polling of booking status. Other components will consume the booking status
 * from the cache using useBookingStatus.
 */
export const usePollBooking = (
  request: { bookingId: string; hotelReferenceId: string },
  overridePollInterval?: 'fast' | 'default' | 'disabled'
) => {
  const invalidateBooking = useInvalidateBookingSummary({
    hotelSlug: request.hotelReferenceId,
    bookingId: request.bookingId,
  });

  const [latestBookingStatus, setLatestBookingStatus] =
    useState<V2BookingStatus>('NONE');

  const [paymentStatus] = useBookingPaymentStatus(request.bookingId);

  const args = {
    hotelReferenceId: request.hotelReferenceId,
    bookingId: request.bookingId,
  };
  const bookingStatus = useBookingStatus(args);
  const status = bookingStatus?.status;

  // We want a faster refresh rate if we're waiting for the payment to be confirmed
  const refetchInterval = match({
    overridePollInterval,
    paymentStatus,
    latestBookingStatus,
  })
    .with(
      // If the booking is confirmed, cancelled, has failed during payment or has expired, disable polling
      P.union(
        { latestBookingStatus: 'CONFIRMED' },
        { latestBookingStatus: 'CANCELLED_BY_USER' },
        { latestBookingStatus: 'PAYMENT_ERROR' },
        { latestBookingStatus: 'EXPIRED' }
      ),
      () => false as const
    )
    // Give preference to overrides
    .with({ overridePollInterval: 'fast' }, () => fastPollInterval)
    .with({ overridePollInterval: 'default' }, () => defaultPollInterval)
    .with({ overridePollInterval: 'disabled' }, () => false as const)

    // Fast poll when submitting a payment, or waiting for cancellation status change
    .with({ paymentStatus: 'submitting' }, () => fastPollInterval)
    .with({ latestBookingStatus: 'CANCEL_RECEIVED' }, () => fastPollInterval)
    .otherwise(() => defaultPollInterval);

  const result = useBookingStatusQuery(args, { refetchInterval });

  useEffect(() => {
    setLatestBookingStatus(status ?? 'NONE');
  }, [status]);

  useEffect(() => {
    // If the status has changed, we should fetch a fresh copy of the booking
    if (latestBookingStatus !== status) {
      console.log('poll: marking booking as stale, refetching...');
      void invalidateBooking();
    }
  }, [status, latestBookingStatus, invalidateBooking]);

  return { bookingStatus: result.data, error: result.error };
};
